'use client';

import { Languages } from 'lucide-react';
import { useLocale } from 'next-intl';

import { routing } from '@kit/i18n';
import { usePathname } from '@kit/i18n/navigation';
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@kit/ui/dropdown-menu';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { usePersistLocale } from '../hooks/use-persist-locale';

/**
 * Language, in the avatar menu beside Theme.
 *
 * It already exists on two settings pages and was still reported as missing,
 * which is the answer: nobody goes to Settings to change a language. Theme
 * lives here for the same reason, and the two are the same kind of choice — how
 * this person wants to read the app, not how the workspace is configured.
 *
 * Deliberately a duplicate placement rather than a move. Settings is where
 * someone looks when told "it is in your settings"; this is where they look
 * when they just want to switch. Both write the same preference.
 *
 * Renders nothing when one locale is configured — a menu that opens onto a
 * single choice is worse than no menu.
 */
export function SubMenuLanguageToggle({ userId }: { userId: string }) {
  const { locales } = routing;
  const current = useLocale();
  const pathname = usePathname();
  const persist = usePersistLocale(userId);

  /*
   * Endonyms — each language named in itself.
   *
   * `Intl.DisplayNames` labels every option in the *current* locale, which is
   * exactly wrong here: an Arabic console would offer English as "الإنجليزية"
   * to the one person who cannot read it. A language picker is the single
   * control that cannot assume you understand the language it is drawn in.
   */
  const label = (locale: string) =>
    new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale;

  if (locales.length <= 1) {
    return null;
  }

  return (
    <DropdownMenuGroup>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          className={'flex w-full items-center justify-between gap-x-2'}
          data-test={'language-toggle'}
        >
          <Languages className={'h-4'} />

          <Trans i18nKey={'account.language'} />

          {/*
           * The current language, in its own script, on the trigger itself.
           *
           * Someone who cannot read the interface needs to recognise this row
           * before they can open it. A globe beside "العربية" reads as a
           * language control to anyone; the word "اللغة" alone does not.
           */}
          <span className={'text-muted-foreground ms-auto text-xs'}>
            {label(current)}
          </span>
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>
          {locales.map((locale) => (
            <DropdownMenuItem
              className={cn('cursor-pointer', {
                'bg-muted': locale === current,
              })}
              data-test={`language-option-${locale}`}
              key={locale}
              onClick={() => {
                /*
                 * Save first, then reload through the locale-prefixed path.
                 *
                 * The middleware owns the cookie: `/ar/…` replies 307 with
                 * `set-cookie: NEXT_LOCALE=ar` and sends you back to the
                 * unprefixed URL. It has to be a real load, because `dir` and
                 * `lang` are on `<html>` in the root layout and a client
                 * navigation never re-renders that.
                 *
                 * The save is awaited because the navigation cancels anything
                 * still in flight.
                 */
                void persist(locale).then(() =>
                  window.location.assign(
                    `/${locale}${pathname === '/' ? '' : pathname}`,
                  ),
                );
              }}
            >
              <span className={'capitalize'}>{label(locale)}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </DropdownMenuGroup>
  );
}
